import { Button } from "@mui/material";
import React, { useEffect, useState, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ApiSearch from "../../components/ApiSearch";
import ClientTable from "../../components/ClientTable";
import Dash from "../../components/Dash";
import Layout from "../../components/Layout/Layout";
import Loading from "../../components/Loading";
import CustomModal, {
  ModalDatePicker,
  ModalInput,
  ModalSelect3,
} from "../../components/Modal";
import InvoicesModal from "../../components/modals/InvoicesModal";
import ChallanModal from "../../components/modals/ChallanModal";
import CustomerQrAccessModal from "../../components/modals/CustomerQrAccessModal";
import QrTemplateModal from "../../components/modals/QrTemplateModal";
import {
  addGSTToCusPro,
  addRateToCusPro,
  getAllCustomers,
  getCustomersById,
  getInvoiceNo,
  getProductData,
  getProductGstByCustomer,
  getProductPriceByCustomer,
  isGstInvoiceNumberValid,
  submitInvoiceDetail,
  updateGSTToCusPro,
  updateRateToCusPro,
} from "../../utils/api";
import { formatDate } from "../../utils/formatData";
import useStore from "../../utils/store";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const calculateTotalCustomerWeight = (records = []) => {
  const challanMap = new Map();
  let standaloneWeight = 0;

  for (const r of records) {
    const w = Number(r?.challanWeight || r?.weight || 0);
    if (w <= 0) continue;

    if (r?.challanId) {
      const key = r.challanId;
      if (!challanMap.has(key)) {
        challanMap.set(key, w);
      }
    } else if (r?.challanDate) {
      const key = new Date(r.challanDate).toISOString();
      if (!challanMap.has(key)) {
        challanMap.set(key, w);
      }
    } else if (r?.dispatchDate) {
      const key = new Date(r.dispatchDate).toISOString();
      if (!challanMap.has(key)) {
        challanMap.set(key, w);
      }
    } else {
      standaloneWeight += w;
    }
  }

  let total = standaloneWeight;
  for (const val of challanMap.values()) {
    total += val;
  }
  return total;
};

const CustomerInfo = ({
  customer,
  currentCustomer,
  customerId,
  onCreateInvoice,
  onUpdate,
}) => {
  const user = useStore((state) => state.user);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [product, setProduct] = useState(null);
  const [rate, setRate] = useState(null);
  const [gst, setGst] = useState(null);
  const [products, setProducts] = useState(null);
  const [rateId, setRateId] = useState(null);
  const [gstId, setGstId] = useState(null);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [qrAccessModalOpen, setQrAccessModalOpen] = useState(false);

  const getProductPriceByCustomerHandler = async (id, productId) => {
    const [success, data] = await getProductPriceByCustomer(id, productId);
    if (success) {
      setRate(data?.rate || "");
      setRateId(data?._id || null);
    }
  };

  const getProductGstByCustomerHandler = async (id, productId) => {
    const [success, data] = await getProductGstByCustomer(id, productId);
    if (success) {
      setGst(data?.gst || "");
      setGstId(data?._id || null);
    }
  };

  const submitHandler = async () => {
    setLoading(true);
    let success = false;
    if (rateId) {
      const [success1] = await updateRateToCusPro(rateId, {
        customerId,
        rate: Number(rate),
        productId: product,
      });
      success = success1;
    } else {
      const [success1] = await addRateToCusPro({
        customerId,
        rate: Number(rate),
        productId: product,
      });
      success = success1;
    }
    let success2 = false;
    if (gstId) {
      const [success] = await updateGSTToCusPro(gstId, {
        customerId,
        gst: Number(gst),
        productId: product,
      });
      success2 = success;
    } else {
      const [success] = await addGSTToCusPro({
        customerId,
        gst: Number(gst),
        productId: product,
      });
      success2 = success;
    }

    if (success && success2) {
      toast.success("Rate updated successfully");
      setOpen(false);
      onUpdate?.();
      window.dispatchEvent(new Event("plabel-data-updated"));
      localStorage.setItem("plabel_last_sync", Date.now().toString());
    } else {
      toast.error("Failed to update rate");
    }
    setLoading(false);
  };

  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 flex flex-col gap-3">
      {/* Top Header Row */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center font-black text-base shadow-xs shrink-0">
            👤
          </div>
          <div>
            <h1 className="text-base font-extrabold text-slate-900 tracking-tight flex items-center gap-2">
              <span>{customer?.name}</span>
            </h1>
            <div className="flex items-center gap-3 text-xs text-slate-500 font-medium mt-0.5">
              <span className="inline-flex items-center gap-1">
                📞 {currentCustomer?.phoneNumber || customer?.phoneNumber || "-"}
              </span>
              {customer?.gstNumber && (
                <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-800 px-1.5 py-0.5 rounded font-mono text-[11px]">
                  GST: {customer.gstNumber}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons Toolbar */}
        <div className="flex items-center gap-2 flex-wrap">
          {Boolean(customer?.qrCodeEnabled || currentCustomer?.qrCodeEnabled) ? (
            <>
              <button
                type="button"
                className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs px-3.5 py-2 rounded-xl shadow-sm hover:shadow transition active:scale-95 cursor-pointer whitespace-nowrap"
                onClick={() => setQrModalOpen(true)}
                title="Open QR Label Builder & Print Preview"
              >
                <span>📱</span>
                <span>QR Code</span>
              </button>
              <button
                type="button"
                className="inline-flex items-center gap-1.5 bg-slate-800 hover:bg-slate-900 text-emerald-400 font-bold text-xs px-3 py-2 rounded-xl shadow-sm hover:shadow transition active:scale-95 cursor-pointer whitespace-nowrap border border-emerald-500/30"
                onClick={() => setQrAccessModalOpen(true)}
                title="Manage Customer QR Login & Password"
              >
                <span>⚙️</span>
                <span>QR Access</span>
              </button>
            </>
          ) : (
            <button
              type="button"
              className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs px-3.5 py-2 rounded-xl shadow-sm hover:shadow transition active:scale-95 cursor-pointer whitespace-nowrap"
              onClick={() => setQrAccessModalOpen(true)}
              title="Enable QR Code Access for this Customer"
            >
              <span>📱</span>
              <span>Enable QR</span>
            </button>
          )}
          <ChallanModal
            customerId={customerId}
            customerName={customer?.name}
            onUpdate={onUpdate}
          />
          <InvoicesModal id={customerId} onUpdate={onUpdate} />
          <button
            type="button"
            className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs px-3.5 py-2 rounded-xl shadow-sm hover:shadow transition active:scale-95 cursor-pointer whitespace-nowrap"
            onClick={onCreateInvoice}
          >
            <span>➕</span>
            <span>Create Bill</span>
          </button>
          {user?.type !== "ViewAdmin" && (
            <button
              type="button"
              className="inline-flex items-center gap-1.5 bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs px-3.5 py-2 rounded-xl shadow-sm hover:shadow transition active:scale-95 cursor-pointer whitespace-nowrap"
              onClick={async () => {
                setLoading(true);
                const [success, data] = await getProductData();
                if (success) {
                  setProducts(data);
                  setOpen(true);
                }
                setLoading(false);
              }}
            >
              <span>🏷️</span>
              <span>Add Rate / GST</span>
            </button>
          )}
          <Link
            to={`/admin/ledger/${customerId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs px-3.5 py-2 rounded-xl shadow-sm hover:shadow transition active:scale-95 cursor-pointer whitespace-nowrap"
          >
            <span>📖</span>
            <span>Ledger</span>
          </Link>
        </div>
      </div>

      {/* Metric Badges Ribbon */}
      <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-slate-100 text-xs">
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg font-semibold bg-slate-50 text-slate-700 border border-slate-200">
          🏷️ Wagon: <strong className="text-slate-900">{customer?.wagon || 0}</strong>
        </span>
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg font-semibold bg-slate-50 text-slate-700 border border-slate-200">
          🏷️ Tag: <strong className="text-slate-900">{(customer?.wagon || 0) * 580}</strong>
        </span>
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200">
          ✅ Completed Tag: <strong className="text-emerald-950">{customer?.orderTagAmount || 0}</strong>
        </span>
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg font-semibold bg-amber-50 text-amber-800 border border-amber-200">
          ⚖️ Balance: <strong className="text-amber-950">{customer?.bal || 0}</strong>
        </span>
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg font-semibold bg-indigo-50 text-indigo-800 border border-indigo-200">
          ⚖️ Total Weight: <strong className="text-indigo-950">{customer?.totalWeight !== undefined && customer?.totalWeight !== null ? `${customer.totalWeight} kg` : `${calculateTotalCustomerWeight(customer?.records || [])} kg`}</strong>
        </span>
      </div>


      <CustomModal
        open={open}
        setOpen={setOpen}
        title={rate ? "Update Rate" : "Add Rate"}
        button={<div className="flex gap-2"></div>}
      >
        <ModalSelect3
          title="Product"
          options={products
            ?.filter((item) => item.productType === "Sale")
            .map((item) => ({
              label: item.productName,
              value: item._id,
            }))}
          onChange={(e) => {
            const pid = e.target.value;
            setProduct(pid);
            if (pid && customerId) {
              getProductPriceByCustomerHandler(customerId, pid);
              getProductGstByCustomerHandler(customerId, pid);
            }
          }}
        />
        {product && (
          <>
            <div className="flex flex-col gap-2">
              <label className="text-black font-semibold">Rate</label>
              <input
                type="number"
                placeholder="Rate"
                className="border border-black rounded p-2"
                disabled={!customer}
                value={rate || ""}
                onChange={(e) => {
                  setRate(e.target.value);
                }}
              />
            </div>
            <button
              className="bg-primary text-white rounded p-2 ml-auto"
              disabled={!customer}
              onClick={async () => {
                if (rate === null || rate === undefined || rate === "") {
                  toast.error("Please enter rate");
                  return;
                }

                if (!customer) {
                  toast.error("Please select customer and product");
                  return;
                }

                setLoading(true);
                if (!rateId) {
                  const [success] = await addRateToCusPro({
                    customerId,
                    productId: product,
                    rate: +rate,
                  });
                  if (success) {
                    toast.success("Rate added successfully");
                    getProductPriceByCustomerHandler(customerId, product);
                    onUpdate?.();
                    window.dispatchEvent(new Event("plabel-data-updated"));
                    localStorage.setItem("plabel_last_sync", Date.now().toString());
                  }
                } else {
                  const [success] = await updateRateToCusPro(rateId, {
                    rate: +rate,
                  });
                  if (success) {
                    toast.success("Rate updated successfully");
                    getProductPriceByCustomerHandler(customerId, product);
                    onUpdate?.();
                    window.dispatchEvent(new Event("plabel-data-updated"));
                    localStorage.setItem("plabel_last_sync", Date.now().toString());
                  }
                }
                setLoading(false);
              }}
            >
              {!loading ? (rateId ? "Update Rate" : "Add Rate") : "Loading..."}
            </button>
            <div className="flex flex-col gap-2">
              <label className="text-black font-semibold">GST</label>
              <input
                type="number"
                placeholder="GST"
                className="border border-black rounded p-2"
                disabled={!customer}
                value={gst || ""}
                onChange={(e) => {
                  setGst(e.target.value);
                }}
              />
            </div>
            <button
              className="bg-primary text-white rounded p-2 ml-auto"
              disabled={!product || !customer}
              onClick={async () => {
                if (gst === null || gst === undefined || gst === "") {
                  toast.error("Please enter gst");
                  return;
                }

                if (!customer || !product) {
                  toast.error("Please select customer and product");
                  return;
                }

                setLoading(true);
                if (!gstId) {
                  const [success] = await addGSTToCusPro({
                    customerId,
                    productId: product,
                    gst: +gst,
                  });
                  if (success) {
                    toast.success("Gst added successfully");
                    getProductGstByCustomerHandler(customerId, product);
                    onUpdate?.();
                    window.dispatchEvent(new Event("plabel-data-updated"));
                    localStorage.setItem("plabel_last_sync", Date.now().toString());
                  }
                } else {
                  const [success] = await updateGSTToCusPro(gstId, {
                    gst: +gst,
                  });
                  if (success) {
                    toast.success("Gst updated successfully");
                    getProductGstByCustomerHandler(customerId, product);
                    onUpdate?.();
                    window.dispatchEvent(new Event("plabel-data-updated"));
                    localStorage.setItem("plabel_last_sync", Date.now().toString());
                  }
                }
                setLoading(false);
              }}
            >
              {!loading ? (gstId ? "Update Gst" : "Add Gst") : "Loading..."}
            </button>
          </>
        )}
      </CustomModal>

      {/* QR Code Template Designer Modal */}
      <QrTemplateModal
        open={qrModalOpen}
        setOpen={setQrModalOpen}
        customer={{
          ...currentCustomer,
          ...customer,
          _id: customerId || customer?._id || currentCustomer?._id,
        }}
        onSaved={onUpdate}
      />

      {/* Customer QR Access & Credentials Modal */}
      <CustomerQrAccessModal
        open={qrAccessModalOpen}
        setOpen={setQrAccessModalOpen}
        customer={{
          ...currentCustomer,
          ...customer,
          _id: customerId || customer?._id || currentCustomer?._id,
        }}
        reload={onUpdate}
      />
    </div>
  );
};

const InvoiceForm = ({
  isPartialInvoice,
  setIsPartialInvoice,
  invoiceNo,
  setInvoiceNo,
  invoiceDate,
  setInvoiceDate,
  setInvoiceData,
  invoiceData,
  productList,
  setProduct,
  setOrderIds,
  partialQuantity,
  setPartialQuantity,
  weight,
  setWeight,
}) => (
  <>
    <ModalInput
      title="Invoice No"
      onChange={(e) => {
        setInvoiceNo(e.target.value);
      }}
      value={invoiceNo}
    />
    <ModalDatePicker
      title="Invoice Date"
      onChange={(e) => {
        setInvoiceDate(e.target.value);
        setInvoiceData({
          ...invoiceData,
          invoiceDate: e.target.value + "T13:40:31.520Z",
        });
      }}
      value={invoiceDate}
    />
    <ModalInput
      title="Note"
      onChange={(e) => {
        setInvoiceData({ ...invoiceData, note: e.target.value });
      }}
      value={invoiceData?.note || ""}
    />

    <ModalSelect3
      title="Product"
      options={productList?.map((item) => ({
        label: `${item.name} (${item.quantity})`,
        value: item._id,
      }))}
      onChange={(e) => {
        setOrderIds(
          productList?.find((item) => item._id === e.target.value)?.orderIds
        );
        setProduct(e.target.value);
        setPartialQuantity(
          productList?.find((item) => item._id === e.target.value)?.quantity
        );
      }}
    />

    {isPartialInvoice && (
      <ModalInput
        title="Quantity"
        type="number"
        onChange={(e) => {
          setPartialQuantity(e.target.value);
        }}
        value={partialQuantity}
      />
    )}

    <ModalInput
      title="Weight"
      type="number"
      onChange={(e) => {
        setWeight(e.target.value);
      }}
      value={weight || ""}
    />
  </>
);

const Customer = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const query = useQuery();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [, setEdit] = useState(null);
  const startDate = useStore((state) => state.startDate);
  const endDate = useStore((state) => state.endDate);
  const [createInvoice, setCreateInvoice] = useState(false);
  const [invoiceNo, setInvoiceNo] = useState("");
  const [invoiceData, setInvoiceData] = useState(null);
  const [customerId, setCustomerId] = useState("");
  const [productList, setProductList] = useState(null);
  const [product, setProduct] = useState(null);
  const [invoiceDate, setInvoiceDate] = useState(null);
  const [orderIds, setOrderIds] = useState(null);
  const [data, setData] = useState({});
  const [currentCustomer, setCurrentCustomer] = useState(null);
  const [isPartialInvoice, setIsPartialInvoice] = useState(true);
  const [partialQuantity, setPartialQuantity] = useState("");
  const [weight, setWeight] = useState("");

  const getAllCustomerHandler = async (silent = false) => {
    if (!silent) setLoading(true);
    const [success, resData] = await getAllCustomers(startDate, endDate);
    if (success) {
      setCustomers(
        resData.map((item) => ({
          ...item,
          name: `${item.userName} ${
            item?.group?.name !== undefined ? `(${item?.group?.name})` : ""
          }`,
        }))
      );
    }
    if (!silent) setLoading(false);
  };

  useEffect(() => {
    getAllCustomerHandler();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const getCustomersByIdHandler = async (id, name, silent = false) => {
    if (!id) return;
    if (!silent) setLoading(true);
    const [success, resData] = await getCustomersById(id, startDate, endDate);
    if (success) {
      setCustomer((prev) => ({
        ...resData,
        name: name || prev?.name || resData?.name,
      }));
      setCustomerId(id);
      setCurrentCustomer((prev) => {
        const found = customers?.find((item) => item._id === id);
        return found || prev;
      });
    }
    if (!silent) setLoading(false);
  };

  const refreshAll = async (silent = true) => {
    const activeId = customerId || query?.get("id") || data?.cur;
    if (!activeId) {
      await getAllCustomerHandler(silent);
      return;
    }
    const found = customers.find((item) => item._id === activeId);
    const activeName = found?.name || found?.userName || customer?.name;
    await Promise.all([
      getAllCustomerHandler(silent),
      getCustomersByIdHandler(activeId, activeName, silent),
    ]);
  };

  // Live Sync on Change: Only refreshes when an action or data change happens
  useEffect(() => {
    const handleDataUpdated = () => {
      refreshAll(true);
    };

    const handleStorage = (e) => {
      if (e.key === "plabel_last_sync") {
        refreshAll(true);
      }
    };

    window.addEventListener("plabel-data-updated", handleDataUpdated);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("plabel-data-updated", handleDataUpdated);
      window.removeEventListener("storage", handleStorage);
    };
  }, [customerId, data?.cur, startDate, endDate]);

  const createInvoicePayload = (isWhatsApp = false) => {
    const payload = {
      ...invoiceData,
      invoiceNumberString: invoiceNo,
      customerId: customerId,
      orderIds: orderIds,
      isWhatsApp: isWhatsApp,
      sendWhatsApp: isWhatsApp,
      ...(isPartialInvoice && { quantity: partialQuantity }),
    };

    if (weight && weight.trim() !== "") {
      payload.weight = Number(weight);
    }

    return payload;
  };

  const resetInvoiceState = () => {
    setCreateInvoice(false);
    setOrderIds(null);
    setPartialQuantity("");
    setWeight("");
    setInvoiceData(null);
    setInvoiceDate(null);
  };

  const invoiceHandler = async (isPrint = false, isWhatsApp = false) => {
    if (!customerId) {
      toast.error("Please select a customer first");
      return;
    }

    if (isPartialInvoice) {
      const selectedProduct = productList?.find((item) => item._id === product);
      const maxQuantity = selectedProduct?.quantity || 0;
      const valid =
        partialQuantity &&
        parseFloat(partialQuantity) > 0 &&
        parseFloat(partialQuantity) <= maxQuantity;

      if (!valid) {
        toast.error(
          `Please enter quantity between 1 and ${maxQuantity}`
        );
        return;
      }
    }

    if (!productList || productList.length === 0) {
      toast.error("Is customer ke paas bill generate karne ke liye koi pending Sale Order nahi hai.");
      return;
    }

    if (!orderIds || (Array.isArray(orderIds) && orderIds.length === 0)) {
      toast.error("Please select a product from dropdown");
      return;
    }

    setLoading(true);
    const endpoint = isPartialInvoice
      ? "/admin/invoice-partial"
      : "/admin/invoice";
    const payload = createInvoicePayload(isWhatsApp);

    const [success, resData] = await submitInvoiceDetail(payload, endpoint);

    if (success) {
      resetInvoiceState();
      if (isWhatsApp) {
        toast.success("✅ Invoice Created & WhatsApp Sent Successfully!");
      } else {
        toast.success("Invoice Created successfully");
      }
      window.dispatchEvent(new Event("plabel-data-updated"));
      localStorage.setItem("plabel_last_sync", Date.now().toString());

      if (isPrint) {
        if (isWhatsApp) {
          window.open(`/admin/invoice/${resData._id}`, "_blank");
        } else {
          window.open(`/admin/invoice/${resData._id}?type=white`, "_blank");
        }
      }
      await refreshAll(false);
    } else {
      toast.error(
        typeof resData === "string"
          ? resData
          : resData?.response?.data?.message ||
              resData?.message ||
              "Failed to create invoice"
      );
    }
    setLoading(false);
  };

  const invoiceNoHandler = async (customerId) => {
    setInvoiceLoading(true);
    const [success, resData] = await getInvoiceNo({
      customerId: customerId,
    });
    if (success) {
      setInvoiceNo(resData.invoiceNumberString);
      setProductList(resData.products);
    }
    setInvoiceLoading(false);
  };

  // Auto-restore / persist selected customer from URL or LocalStorage
  useEffect(() => {
    const urlId = query?.get("id");
    const savedId = localStorage.getItem("selectedCustomerAdminId");
    const targetId = urlId || savedId;

    if (targetId && customers.length > 0) {
      const found = customers.find((item) => item._id === targetId);
      if (found) {
        setData({
          cur: found._id,
        });
        getCustomersByIdHandler(found._id, found.name || found.userName);
        if (!urlId) {
          navigate(`/admin/customer?id=${found._id}`, { replace: true });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query?.get("id"), customers]);

  return (
    <Layout>
      {loading && <Loading />}
      <div className="flex gap-3 flex-col md:flex-row w-full text-[90%]">
        <ApiSearch
          active={data?.cur}
          data={customers
            .filter((item) => item?.type === "Customer")
            .map((item) => ({
              ...item,
              userName: `${item.userName} ${
                item?.group?.name !== undefined ? `(${item?.group?.name})` : ""
              }`,
            }))}
          item={(item) => {
            const custName = item?.name || item?.userName;
            getCustomersByIdHandler(item._id, custName);
            setData({
              cur: item._id,
            });
            localStorage.setItem("selectedCustomerAdminId", item._id);
            navigate(`/admin/customer?id=${item._id}`, { replace: true });
          }}
          customer
        />
        {customer && (
          <div className="flex flex-col gap-3 md:w-[calc(100%-18rem)] flex-1">
            <CustomerInfo
              customer={customer}
              currentCustomer={currentCustomer || customers?.find((item) => item._id === customerId)}
              customerId={customerId}
              onUpdate={() => refreshAll(true)}
              onCreateInvoice={() => {
                const today = new Date();
                const todayStr = `${today.getFullYear()}-${String(
                  today.getMonth() + 1
                ).padStart(2, "0")}-${String(today.getDate()).padStart(
                  2,
                  "0"
                )}`;
                setInvoiceDate(todayStr);
                setInvoiceData({
                  invoiceDate: todayStr + "T13:40:31.520Z",
                });
                const m = today.getMonth() + 1;
                const y = today.getFullYear();
                const yrStr = m < 4 ? `${y - 1}-${y.toString().substring(2)}` : `${y}-${(y + 1).toString().substring(2)}`;
                setInvoiceNo(`${yrStr}/...`);
                invoiceNoHandler(customerId);
                setCreateInvoice(true);
              }}
            />
            <div className="flex gap-4 flex-col md:flex-row">
              <Dash title={"Sale"} value={customer?.totalSales?.toFixed(2)} />
              <Dash
                title={"Payment Received"}
                value={customer?.totalPaymentRecived?.toFixed(2)}
              />
              <Dash
                title={"Balance"}
                value={customer?.totalBalance?.toFixed(2)}
              />
            </div>
            <ClientTable
              customers={customers}
              id={data?.cur}
              reload={() => refreshAll(true)}
              reload2={() => refreshAll(true)}
              edit={(itemData) => {
                setEdit(true);
                if (itemData.paymentType) {
                  setData({
                    _id: itemData._id,
                    customerId: itemData?.customer?._id,
                    description: itemData.description,
                    paymentType: itemData.paymentType,
                    amount: itemData.amount,
                  });
                } else {
                  setData({
                    _id: itemData._id,
                    customerId: itemData?.customer?._id,
                    description: itemData.description,
                    group: itemData.group,
                    quantity: itemData.quantity,
                    rate: itemData.rate,
                    gst: itemData.gst,
                    totalAmount: itemData.totalAmount,
                  });
                }
              }}
              data={[...(customer?.records ?? [])]}
              columns={[
                {
                  title: "Date",
                  dataIndex: "date",
                  render: (record) => formatDate(record?.date),
                },
                {
                  title: "Customer",
                  dataIndex: "customer",
                  render: (item) => {
                    return item.customer.name;
                  },
                },
                {
                  title: "description",
                  dataIndex: "description",
                  render: (item) => {
                    return `${item.description} ${
                      item.dispatchNote ? `,${item.dispatchNote}` : ""
                    }`;
                  },
                },
                {
                  title: "Items",
                  dataIndex: "group",
                },
                {
                  title: "Qnt.",
                  dataIndex: "quantity",
                },
                {
                  title: "Rate",
                  dataIndex: "rate",
                },
                {
                  title: "GST",
                  dataIndex: "gst",
                },
                {
                  title: "Total",
                  dataIndex: "totalAmount",
                },
              ]}
            />
          </div>
        )}
      </div>
      <CustomModal
        open={createInvoice}
        title="Create Bill"
        setOpen={() => {
          setCreateInvoice(false);
          setPartialQuantity("");
          setWeight("");
        }}
        button={
          <div className="flex gap-4 items-center flex-wrap">
            <Button
              variant="contained"
              color="primary"
              disabled={invoiceLoading || !productList || productList.length === 0}
              onClick={() => invoiceHandler(true, true)}
            >
              {invoiceLoading ? "Loading..." : "WhatsApp"}
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={invoiceLoading || !productList || productList.length === 0}
              onClick={() => invoiceHandler(true, false)}
            >
              Print
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => invoiceHandler(false)}
              disabled={invoiceLoading || !productList || productList.length === 0}
            >
              Save
            </Button>
          </div>
        }
        loading={invoiceLoading}
      >
        <InvoiceForm
          isPartialInvoice={isPartialInvoice}
          setIsPartialInvoice={setIsPartialInvoice}
          invoiceNo={invoiceNo}
          setInvoiceNo={setInvoiceNo}
          invoiceDate={invoiceDate}
          setInvoiceDate={setInvoiceDate}
          setInvoiceData={setInvoiceData}
          invoiceData={invoiceData}
          productList={productList}
          setOrderIds={setOrderIds}
          partialQuantity={partialQuantity}
          setPartialQuantity={setPartialQuantity}
          setProduct={setProduct}
          weight={weight}
          setWeight={setWeight}
        />
      </CustomModal>

    </Layout>
  );
};

export default Customer;
